import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './ModelFirmwareVersionList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelFirmwareVersionState } from '../../reducers/initialState';
import { useParams, Link } from 'react-router-dom';
import ModelFirmwareVersionListTable from '../ModelFirmwareVersionList/ModelFirmwareVersionListTable';
import { IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { WidgetVisibility } from '../../utils/widgetManager';
import Button from 'react-bootstrap/Button';
import { compareValues } from '../../utils';
import Cookies from 'js-cookie';
import { getModelCategoryPermission } from '../../utils';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';

const ModelFirmwareVersionList = ({
  getModelFirmwareVersions,
  userModelCategories,
  modelFirmwareVersionManager = {
    ...initialModelFirmwareVersionState.modelFirmwareVersionManager
  },
  intl,
  userProfileWidgets,
  currentTimezone,
  setCurrentPage
}) => {
  let { ModelCategoryId, ModelId } = useParams();
  let filter = modelFirmwareVersionManager.filter || '';
  let offset = modelFirmwareVersionManager.offset || 0;
  let limit = modelFirmwareVersionManager.limit || 1000;
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  let organisationId = Cookies.get(`selectedorganisationid-${envName}`) || 1;

  const [isFilter, setIsFilter] = useState(false);
  const [active, setActive] = useState(false);
  const [searchText, setSearchText] = useState('');


  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };


  const searchHandler = value => {
    if (value === '') {
      setIsFilter(false);
    } else {
      setIsFilter(true);
    }
    setSearchText(value);

    let filteredList = modelFirmwareVersionManager.modelFirmwareVersions.filter(function (item) {
      return item.Name.toLowerCase().includes(value.toLowerCase());
    });

  };

  const filterData = () => {
    let filteredList = modelFirmwareVersionManager.modelFirmwareVersions;
    if (isFilter) {
      filteredList = modelFirmwareVersionManager.modelFirmwareVersions.filter(function (item) {
        return item.Name.toLowerCase().includes(searchText.toLowerCase());
      });
    }

    return filteredList.sort(compareValues('ModelFirmwareVersionId', 'desc'));
  };


  useEffect(() => {
    if (ModelId) {
      getModelFirmwareVersions(ModelId, offset, limit, filter);
    }
  }, [getModelFirmwareVersions, ModelId, offset, limit, filter]);


  let messageId = (modelFirmwareVersionManager && modelFirmwareVersionManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isAddAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELFIRMWAREVERSIONADD');
  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELFIRMWAREVERSIONUPDATE');



  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.manageModelFirmwareVersions" defaultMessage="Model Firmware Versions" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)
  const dataObj = Object.values(filterData());

  return (
    <div className={s.modelFirmwareVersion}>
      {modelFirmwareVersionManager.isLoading && <LoadingSpinner />}
      <Banner
        failureText={messageText}
        showBanner={modelFirmwareVersionManager.showBanner}
        status={modelFirmwareVersionManager.isOpSuccessful}
        successText={messageText}
      />
      <ModelConfigurationContentTemplate selectedPage="manageModelFirmwareVersions" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="model.manageModelFirmwareVersions" defaultMessage="Model Firmware Versions" />
              <span className={s.circle}>
                {modelFirmwareVersionManager.modelFirmwareVersions.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                initialText={filter}
                placeHolderTextId="modelFirmwareVersion.modelFirmwareVersion"
              />
            </Col>
            <Col sm={6} lg={8}>
              {isModelCategoryOwner ? (
                <Link to={`/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/model-firmware-version/add`}>
                  <Button className={s.addbutton} style={WidgetVisibility(userProfileWidgets, `MODELFIRMWAREVERSIONADD`)}>
                    + <FormattedMessage id="modelFirmwareVersion.addNew" defaultMessage="ADD NEW" />
                  </Button>
                </Link>
              ) : (
                ''
              )}
            </Col>
          </Row>

          <div className="">
            <Pagination
              data={dataObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={modelFirmwareVersionManager.currentPage}>
              <ModelFirmwareVersionListTable
                isModelCategoryOwner={isModelCategoryOwner}
                UpdateAllowed={isUpdateAllowed}
                currentTimezone={currentTimezone}
                ModelId={ModelId}
                ModelCategoryId={ModelCategoryId}
              />
            </Pagination>
          </div>
        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};

ModelFirmwareVersionList.propTypes = {
  getModelFirmwareVersions: PropTypes.func.isRequired,
  setPageFilter: PropTypes.func.isRequired,
  currentTimezone: PropTypes.string.isRequired
};

export default injectIntl(ModelFirmwareVersionList);
